import React, { useState } from 'react';
import { Box, Tooltip, Typography, Icon, Popover, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';

interface MessageReadReceiptsProps {
  status?: string;
  deliveredAt?: string;
  metadata?: {
    events?: Array<{
      RecordType?: string;
      Recipient?: string;
      ReceivedAt?: string;
      Client?: {
        Name?: string;
        Family?: string;
      };
      Platform?: string;
    }>;
  };
}

const MessageReadReceipts: React.FC<MessageReadReceiptsProps> = ({ status, deliveredAt, metadata }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Check if message is delivered
  const isDelivered = status === 'delivered' || status === 'sent';
  
  // Check if message has been read (has Open events)
  const openEvents = metadata?.events?.filter(event => event && event.RecordType === 'Open') || [];
  const hasBeenRead = openEvents.length > 0;

  if (!isDelivered) {
    return null;
  }

  // Format delivered at time
  const formatDeliveredTime = () => {
    if (!deliveredAt) return 'Delivered';
    try {
      return `Delivered ${format(new Date(deliveredAt), 'MMM d, h:mm a')}`;
    } catch {
      return 'Delivered';
    }
  };

  // Get unique recipients and their last open time
  const getUniqueOpens = () => {
    const uniqueOpens = new Map();
    
    openEvents.forEach(event => {
      if (!event) return; // Skip null/undefined events
      
      const recipient = event.Recipient || 'Unknown';
      const existingEntry = uniqueOpens.get(recipient);
      
      if (!existingEntry || (event.ReceivedAt && new Date(event.ReceivedAt) > new Date(existingEntry.ReceivedAt))) {
        uniqueOpens.set(recipient, event);
      }
    });
    
    return Array.from(uniqueOpens.values())
      .sort((a, b) => {
        if (!a || !b || !a.ReceivedAt || !b.ReceivedAt) return 0;
        return new Date(b.ReceivedAt).getTime() - new Date(a.ReceivedAt).getTime();
      })
      .slice(0, 5); // Get last 5
  };

  const uniqueOpens = getUniqueOpens();
  const totalOpens = openEvents.length;

  const tooltipContent = hasBeenRead ? (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Opened {totalOpens}+ times
      </Typography>
      {uniqueOpens.map((event, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>
            {event.Platform === 'Desktop' ? 'computer' : 'phone_iphone'}
          </Icon>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {event.Recipient || 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {event.ReceivedAt ? format(new Date(event.ReceivedAt), 'EEE MMM d, h:mm a') : 'Unknown time'}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  ) : formatDeliveredTime();

  return (
    <>
      <Tooltip 
        title={!hasBeenRead ? tooltipContent : ''} 
        placement="left"
        arrow
      >
        <Box
          onClick={hasBeenRead ? handleClick : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ml: 0.5,
            cursor: hasBeenRead ? 'pointer' : 'default',
            color: theme.palette.text.secondary,
            '& .MuiIcon-root': {
              fontSize: '14px',
            },
          }}
        >
          {hasBeenRead ? (
            <>
              <Icon>done_all</Icon>
            </>
          ) : (
            <Icon>done</Icon>
          )}
        </Box>
      </Tooltip>

      {/* Popover for read receipts */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 280,
            maxWidth: 350,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        {tooltipContent}
      </Popover>
    </>
  );
};

export default MessageReadReceipts; 
